import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Map, BarChart3, Zap, Globe, Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { useTrends } from '../hooks/useTrends';
import Skeleton from '../components/ui/Skeleton';

const Home = () => {
  const { trends, loading } = useTrends({ limit: 10 });

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <section className="text-center py-12">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
          Discover What's <span className="gradient-text">Trending</span>
        </h1>
        <p className="text-lg md:text-xl text-reddit-600 dark:text-dark-textMuted max-w-3xl mx-auto mb-8">
          Real-time analysis of trending topics from across the web. 
          Visualize what's hot right now with interactive charts and heatmaps.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Link to="/trends">
            <Button variant="primary" size="lg">
              <TrendingUp className="w-5 h-5" />
              Explore Trends
            </Button>
          </Link>
          <Link to="/heatmap">
            <Button variant="outline" size="lg">
              <Map className="w-5 h-5" />
              View Heatmap
            </Button>
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="w-12 h-12 bg-reddit-orange rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Real-Time Data</h3>
            <p className="text-reddit-600 dark:text-dark-textMuted">
              Get instant updates on trending topics from multiple sources
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="w-12 h-12 bg-reddit-blue rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Global Coverage</h3>
            <p className="text-reddit-600 dark:text-dark-textMuted">
              Track trends across different regions and categories
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <div className="w-12 h-12 bg-reddit-green rounded-full flex items-center justify-center mx-auto mb-4">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Interactive Charts</h3>
            <p className="text-reddit-600 dark:text-dark-textMuted">
              Visualize data with beautiful, interactive visualizations
            </p>
          </CardContent>
        </Card>
      </section>

      {/* Top Trends Preview */}
      <section>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-reddit-orange" />
              Top Trending Now
            </CardTitle>
            <Link to="/trends">
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {trends.map((trend, index) => (
                  <div
                    key={trend.id}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-reddit-50 dark:hover:bg-dark-border transition-colors"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-reddit-100 dark:bg-dark-border font-bold text-reddit-orange">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold">{trend.topic}</h4>
                      <div className="flex items-center gap-2 text-sm text-reddit-600 dark:text-dark-textMuted">
                        <span>{trend.mentions.toLocaleString()} mentions</span>
                        <span>•</span>
                        <Badge variant={trend.growth > 0 ? 'green' : 'orange'} className="text-xs">
                          {trend.growth > 0 ? '+' : ''}{trend.growth}%
                        </Badge>
                        <span>•</span>
                        <span>{trend.source}</span>
                      </div>
                    </div>
                    <Badge variant="blue">{trend.category}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* CTA Section */}
      <section className="reddit-card p-8 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to explore?</h2>
        <p className="text-reddit-600 dark:text-dark-textMuted mb-6 max-w-2xl mx-auto">
          Start analyzing trending topics with powerful visualizations and real-time data insights.
        </p>
        <Link to="/trends">
          <Button variant="primary" size="lg">
            Get Started
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default Home;